import { useUser } from '@/lib/auth';
import { AuthLoader } from '@/lib/auth';
import { LoaderScreen } from '@/components/Elements/LoaderScreen';
import { Navigate, Outlet, useSearchParams } from 'react-router-dom';
import { PRIVATE_TOP_PATH } from '@/constants/routes';
import { ReactNode } from 'react';

type ForceLoginStateProps = {
  type: 'loggedInOnly' | 'guestOnly';
  children?: ReactNode;
};

type PreferredAuth = 'register' | 'login' | null;

export const ForceLoginState = ({ type, children }: ForceLoginStateProps) => {
  const currentURI = encodeURIComponent(window.location.href);
  const { data: user, isLoading } = useUser();
  const [searchParams] = useSearchParams();
  const preferredAuth = searchParams.get('preferred_auth') as PreferredAuth;
  const logoutPending = window.history.state?.logoutPending === true;

  const renderContent = () => {
    return <>{children ?? <Outlet />}</>;
  };

  if (type === 'loggedInOnly') {
    // ログアウトリクエスト直後は OIDC ログアウト画面に遷移させるため、
    // SPA 側でのログイン画面へのリダイレクトや描画切り替えを抑止する
    if (logoutPending) return renderContent();

    if (isLoading) return <LoaderScreen />;

    // 未ログインの場合
    if (user == null) {
      if (preferredAuth === 'register') {
        return <Navigate to={`/auth/register?to=${currentURI}`} />;
      } else {
        return <Navigate to={`/auth/login?to=${currentURI}`} />;
      }
    }

    // ログイン済みで、本登録完了 かつ 本登録画面のアクセスの場合
    if (user.signupCompleted && window.location.pathname === '/setup/profile') {
      return <Navigate to={PRIVATE_TOP_PATH} />;
    }

    // ログイン済みで、本登録未完了 かつ 本登録画面以外のアクセスの場合
    if (
      !user.signupCompleted &&
      window.location.pathname !== '/setup/profile'
    ) {
      return <Navigate to={`/setup/profile?to=${currentURI}`} />;
    }

    return renderContent();
  }

  if (type === 'guestOnly') {
    return (
      <AuthLoader
        renderLoading={() => <LoaderScreen />}
        renderUnauthenticated={renderContent}
      >
        <Navigate to={PRIVATE_TOP_PATH} />
      </AuthLoader>
    );
  }
};
